"use client";

import React from "react";
import { Button } from "@/components/ui/primitives/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/primitives/select";
import { Textarea } from "@/components/ui/primitives/textarea";
import { cn } from "@/lib/utils";
import type { ChatStatus } from "ai";
import { Loader2Icon, ArrowUp, XIcon } from "lucide-react";
import type {
  ComponentProps,
  HTMLAttributes,
  KeyboardEventHandler,
} from "react";
import { Children, forwardRef } from "react";

export type PromptInputProps = HTMLAttributes<HTMLFormElement>;

export const PromptInput = ({ className, ...props }: PromptInputProps) => (
  <form
    className={cn(
      "w-full overflow-hidden rounded-3xl border bg-card shadow-xs dark:bg-background",
      className
    )}
    {...props}
  />
);

export type PromptInputTextareaProps = ComponentProps<typeof Textarea> & {
  minHeight?: number;
  maxHeight?: number;
  placeholderElement?: React.ReactNode;
};

export const PromptInputTextarea = forwardRef<
  HTMLTextAreaElement,
  PromptInputTextareaProps
>(({
  onChange,
  className,
  placeholder = "What would you like to know?",
  placeholderElement,
  ...props
}, ref) => {
  const handleKeyDown: KeyboardEventHandler<HTMLTextAreaElement> = (e) => {
    // Allow standard browser shortcuts like Command+A / Ctrl+A
    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "a") {
      // Allow select all - don't prevent default
      return;
    }

    if (e.key === "Enter") {
      // Don't submit if IME composition is in progress
      if (e.nativeEvent.isComposing) {
        return;
      }

      if (e.shiftKey) {
        // Allow newline
        return;
      }

      // Submit on Enter (without Shift)
      e.preventDefault();
      const form = e.currentTarget.form;
      if (form) {
        form.requestSubmit();
      }
    }
  };

  return (
    <div className="relative">
      {placeholderElement && (
        <div
          className={cn(
            "absolute inset-0 pointer-events-none px-4 py-3 text-muted-foreground",
            "flex items-start",
            props.value && "opacity-0"
          )}
        >
          {placeholderElement}
        </div>
      )}
      <Textarea
        ref={ref}
        className={cn(
          "w-full resize-none rounded-none border-none p-3 shadow-none outline-none ring-0",
          "field-sizing-content max-h-[6lh] bg-transparent dark:bg-transparent",
          "focus-visible:ring-0",
          placeholderElement && "placeholder-transparent",
          className
        )}
        name="message"
        onChange={(e) => {
          onChange?.(e);
        }}
        onKeyDown={handleKeyDown}
        placeholder={placeholderElement ? undefined : placeholder}
        {...props}
      />
    </div>
  );
});

PromptInputTextarea.displayName = "PromptInputTextarea";

export type PromptInputToolbarProps = HTMLAttributes<HTMLDivElement>;

export const PromptInputToolbar = ({
  className,
  ...props
}: PromptInputToolbarProps) => (
  <div
    className={cn("flex items-center justify-between p-2", className)}
    {...props}
  />
);

export type PromptInputToolsProps = HTMLAttributes<HTMLDivElement>;

export const PromptInputTools = ({
  className,
  ...props
}: PromptInputToolsProps) => (
  <div
    className={cn(
      "flex items-center gap-1",
      // "[&_button:first-child]:rounded-bl-xl",
      className
    )}
    {...props}
  />
);

export type PromptInputButtonProps = ComponentProps<typeof Button>;

export const PromptInputButton = ({
  variant = "ghost",
  className,
  size,
  ...props
}: PromptInputButtonProps) => {
  const newSize =
    size ?? Children.count(props.children) > 1 ? "default" : "icon";

  return (
    <Button
      className={cn(
        "shrink-0 gap-1.5 rounded-lg cursor-pointer",
        variant === "ghost" && "text-muted-foreground",
        newSize === "default" && "px-3",
        className
      )}
      size={newSize}
      type="button"
      variant={variant}
      {...props}
    />
  );
};

export type PromptInputSubmitProps = ComponentProps<typeof Button> & {
  status?: ChatStatus;
  /** Явный флаг отмены запроса для более понятной логики */
  isCancelling?: boolean;
};

export const PromptInputSubmit = ({
  className,
  variant = "default",
  size = "icon",
  status,
  children,
  onClick,
  disabled,
  isCancelling = false,
  ...props
}: PromptInputSubmitProps) => {
  let Icon = <ArrowUp className="size-5" />;

  if (status === "submitted") {
    Icon = <Loader2Icon className="size-4 animate-spin" />;
  } else if (status === "streaming") {
    Icon = (
      <svg
        viewBox="0 0 24 24"
        className="size-3.25 shrink-0 fill-current rounded-xs"
        aria-hidden
      >
        <rect width="24" height="24" rx="2" />
      </svg>
    );
  } else if (status === "error") {
    Icon = <XIcon className="size-4" />;
  }

  // Если статус streaming или есть onClick, меняем тип на button чтобы не отправлять форму
  // Это важно для кнопки Cancel во время стриминга
  const buttonType = (status === "streaming" || onClick || isCancelling)
    ? "button"
    : "submit";
  
  // Определяем состояния
  const isStreaming = status === "streaming";
  const isDisabled = disabled;

  // Обработчик клика
  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (onClick) {
      onClick(e);
    }
  };

  return (
    <Button
      className={cn(
        "gap-1.5 rounded-full cursor-pointer",
        isStreaming && !isCancelling && "hover:bg-destructive/10 hover:text-destructive",
        isCancelling && "opacity-50 cursor-not-allowed",
        isDisabled && !isCancelling && "disabled:!opacity-20",
        className
      )}
      size={size}
      variant={variant}
      onClick={onClick ? handleClick : undefined}
      disabled={isDisabled}
      title={
        isCancelling 
          ? "Cancelling..." 
          : status === "streaming" 
            ? "Stop generation" 
            : undefined
      }
      {...props}
      type={buttonType}
    >
      {children ?? Icon}
    </Button>
  );
};

export type PromptInputModelSelectProps = ComponentProps<typeof Select>;

export const PromptInputModelSelect = (props: PromptInputModelSelectProps) => (
  <Select {...props} />
);

export type PromptInputModelSelectTriggerProps = ComponentProps<
  typeof SelectTrigger
>;

export const PromptInputModelSelectTrigger = ({
  className,
  ...props
}: PromptInputModelSelectTriggerProps) => (
  <SelectTrigger
    className={cn(
      "border-none bg-transparent font-medium text-muted-foreground shadow-none transition-colors cursor-pointer",
      'hover:!bg-[var(--background-custom)] dark:hover:!bg-[var(--background-custom)] hover:text-foreground [&[aria-expanded="true"]]:bg-accent [&[aria-expanded="true"]]:text-foreground',
      className
    )}
    {...props}
  />
);

export type PromptInputModelSelectContentProps = ComponentProps<
  typeof SelectContent
>;

export const PromptInputModelSelectContent = ({
  className,
  ...props
}: PromptInputModelSelectContentProps) => (
  <SelectContent
    className={cn(
      "!bg-popover !text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 !z-50 max-h-(--radix-dropdown-menu-content-available-height) w-fit origin-(--radix-dropdown-menu-content-transform-origin) overflow-x-hidden overflow-y-auto !rounded-lg !border !p-0",
      className
    )}
    {...props}
  />
);

export type PromptInputModelSelectItemProps = ComponentProps<typeof SelectItem>;

export const PromptInputModelSelectItem = ({
  className,
  ...props
}: PromptInputModelSelectItemProps) => (
  <SelectItem
    className={cn(
      "relative flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-sm font-[450] text-sidebar-muted-foreground outline-hidden select-none",
      "focus:bg-sidebar-accent focus:text-sidebar-accent-foreground data-[highlighted]:bg-sidebar-accent data-[highlighted]:text-sidebar-accent-foreground",
      "data-[variant=destructive]:text-destructive data-[variant=destructive]:focus:bg-destructive/10 dark:data-[variant=destructive]:focus:bg-destructive/20 data-[variant=destructive]:focus:text-destructive data-[variant=destructive]:*:[svg]:!text-destructive",
      "[&_svg:not([class*='text-'])]:text-sidebar-icon-foreground focus:[&_svg:not([class*='text-'])]:text-sidebar-icon-accent-foreground data-[highlighted]:[&_svg:not([class*='text-'])]:text-sidebar-icon-accent-foreground",
      "data-[disabled]:pointer-events-none data-[disabled]:opacity-50 data-[inset]:pl-8 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
      className
    )}
    {...props}
  />
);

export type PromptInputModelSelectValueProps = ComponentProps<
  typeof SelectValue
>;

export const PromptInputModelSelectValue = ({
  className,
  ...props
}: PromptInputModelSelectValueProps) => (
  <SelectValue className={cn(className)} {...props} />
);
